/**
 * Race drivers: where each finish gap was made (0_race_fleet.py race_fleet.race_drivers, stored on each
 * race of the race_timeline day object; docs/backend/race-stats.md "Race drivers").
 *
 * Every boat's gap to the race winner at the winner's finish, along the course, split into five parts
 * that add up to it. The day view averages each boat's parts over the races it finished (a boat with
 * 3 races is divided by 3), the same per-race rule as the "All" race-summary tables.
 */
import type { RaceTimelineRace } from "./raceTimeline";

export type DriverKey = "start" | "mnvr" | "marks" | "side" | "speed";

export const DRIVER_BUCKETS: { key: DriverKey; label: string; phrase: string; token: string }[] = [
  { key: "start", label: "Start", phrase: "at the start", token: "--color-race-driver-start" },
  { key: "mnvr", label: "Maneuvers", phrase: "in maneuvers", token: "--color-race-driver-mnvr" },
  { key: "marks", label: "Marks", phrase: "at the marks", token: "--color-race-driver-marks" },
  { key: "side", label: "Side", phrase: "on the side of the course", token: "--color-race-driver-side" },
  { key: "speed", label: "Speed", phrase: "on straight-line speed", token: "--color-race-driver-speed" },
];

/** Parts smaller than this (metres) are shown faded and left out of the sentences. */
export const DRIVER_NOISE_M = 20;

export type DriverParts = Record<DriverKey, number>;

export interface RaceDriverBoat extends Partial<DriverParts> {
  status?: "DNF";
  gap_m?: number;
  gap_s?: number | null;
  maneuvers?: number;
  left_pct?: number | null;
  /** The speed part split by phase: metres lost (+) / gained (−) on straight-line seconds with
   *  VMC / VMG >= 1 (in phase) or below it. Fleet passes since 2026-10-10. */
  speed_inphase?: number | null;
  speed_outphase?: number | null;
  /** Share of the boat's straight-line seconds in phase, %. */
  inphase_pct?: number | null;
  /** Per-leg entries (RaceDriverLeg.boats): distance behind the leader at the leg's end. */
  gap_end_m?: number | null;
  /** Per-leg entries: led the whole leg (the leg's reference, zero). */
  led?: boolean;
}

/** Speed part by phase for one row (one race, or the day's per-race average). */
export interface DriverPhase {
  inM: number;
  outM: number;
  pct: number | null;
}

export interface RaceDriverGate {
  mark: string;
  side: "left" | "right";
  boats: { boat: string; dtl_change_m: number | null }[];
}

export interface RaceDriverLeg {
  leg: number;
  upwind: boolean;
  /** reaching / upwind / downwind. Absent on timelines that only stored the upwind boolean. */
  kind?: "reaching" | "upwind" | "downwind";
  /** Positive = the left half closed on the leader faster (metres per minute of straight sailing). */
  left_paid_m_per_min?: number | null;
  left_share_pct?: number | null;
  /** The gate rounded at the END of the leg, with each boat's DTL change to the next mark. */
  gates?: RaceDriverGate[];
  /** Each boat's parts over the leader's leg (previous rounding, or the start, to its rounding at the end
   *  of the leg): gap_m = metres lost (+) / gained (−) on the leader over the leg. Absent on fleet passes
   *  before 2026-10-10. */
  boats?: Record<string, RaceDriverBoat>;
}

export interface RaceDrivers {
  winner: string;
  course_mps: number | null;
  boats: Record<string, RaceDriverBoat>;
  legs: RaceDriverLeg[];
  spread: DriverParts;
  /** The leg at whose end the gaps were measured: below legs_total when the data stopped before
   *  every racing boat finished (compared at the last mark they all rounded). */
  measured_at_leg?: number;
  legs_total?: number;
}

/** One boat's row in the drivers view (one race, or the day's per-race average). */
export interface DriverRow {
  boat: string;
  parts: DriverParts | null;
  gapM: number | null;
  gapS: number | null;
  /** Mean tacks + gybes per race. */
  maneuvers: number | null;
  /** Null when the fleet pass predates the split. */
  phase: DriverPhase | null;
  isWinner: boolean;
  /** Races it finished / raced (day view). */
  finished: number;
  raced: number;
  /** Leg view: distance behind the leader at the leg's end. */
  gapEndM?: number | null;
}

export interface DriversView {
  rows: DriverRow[];
  spread: DriverParts;
  /** Metres per second along the course, for metres -> seconds. */
  mps: number | null;
  raceCount: number;
  roundingsPerRace: number;
  /** Races (by number) compared at a mark because the data stopped before the finish, with that leg. */
  measuredEarly: { race: number; leg: number; legs: number }[];
}

const ZERO: DriverParts = { start: 0, mnvr: 0, marks: 0, side: 0, speed: 0 };

function partsOf(b: RaceDriverBoat): DriverParts | null {
  if (b.status === "DNF" || b.gap_m == null) {return null;}
  const out = { ...ZERO };
  for (const { key } of DRIVER_BUCKETS) {out[key] = Number(b[key] ?? 0);}
  return out;
}

function phaseOf(b: RaceDriverBoat): DriverPhase | null {
  if (b.status === "DNF" || b.speed_inphase == null || b.speed_outphase == null) {return null;}
  return { inM: b.speed_inphase, outM: b.speed_outphase, pct: b.inphase_pct ?? null };
}

function spreadOf(rows: DriverRow[]): DriverParts {
  const out = { ...ZERO };
  const others = rows.filter((r) => r.parts && !r.isWinner);
  for (const { key } of DRIVER_BUCKETS) {
    const vals = others.map((r) => (r.parts as DriverParts)[key]);
    out[key] = vals.length ? Math.max(...vals) - Math.min(Math.min(...vals), 0) : 0;
  }
  return out;
}

/** The drivers of one race, restricted to `sources` (empty = every boat). */
export function raceDriversView(race: RaceTimelineRace, sources: string[]): DriversView | null {
  const d = race.drivers;
  if (!d) {return null;}
  const want = sources.length ? new Set(sources) : null;
  const rows: DriverRow[] = Object.entries(d.boats)
    .filter(([boat]) => !want || want.has(boat))
    .map(([boat, b]) => ({
      boat,
      parts: partsOf(b),
      gapM: b.gap_m ?? null,
      gapS: b.gap_s ?? null,
      maneuvers: b.maneuvers ?? null,
      phase: phaseOf(b),
      isWinner: boat === d.winner,
      finished: b.status === "DNF" ? 0 : 1,
      raced: 1,
    }));
  rows.sort(byGap);
  return {
    rows,
    spread: spreadOf(rows),
    mps: d.course_mps,
    raceCount: 1,
    roundingsPerRace: Math.max(0, (race.legs?.length ?? 1) - 1),
    measuredEarly: early(race),
  };
}

/** One leg of a race (1-based), restricted to `sources`; null when the fleet pass predates per-leg parts.
 *  The reference is the boat that led the whole leg (none when the lead changed). */
export function legDriversView(race: RaceTimelineRace, leg: number, sources: string[]): DriversView | null {
  const l = race.drivers?.legs?.find((x) => x.leg === leg);
  if (!l?.boats) {return null;}
  const want = sources.length ? new Set(sources) : null;
  const rows: DriverRow[] = Object.entries(l.boats)
    .filter(([boat]) => !want || want.has(boat))
    .map(([boat, b]) => ({
      boat,
      parts: partsOf(b),
      gapM: b.gap_m ?? null,
      gapS: b.gap_s ?? null,
      maneuvers: b.maneuvers ?? null,
      phase: phaseOf(b),
      isWinner: b.led === true,
      finished: b.status === "DNF" ? 0 : 1,
      raced: 1,
      gapEndM: b.gap_end_m ?? null,
    }));
  rows.sort(byGap);
  return {
    rows,
    spread: spreadOf(rows),
    mps: race.drivers?.course_mps ?? null,
    raceCount: 1,
    // One mark window opens the leg and another closes it, so "per rounding" would mislead.
    roundingsPerRace: 0,
    measuredEarly: [],
  };
}

/** One leg over the day: each boat's leg parts averaged over the races it was measured on that leg
 *  (the dayDriversView rule); a boat that led the leg in every race is the reference. */
export function dayLegDriversView(races: RaceTimelineRace[], leg: number, sources: string[]): DriversView | null {
  const asRaces: RaceTimelineRace[] = [];
  for (const race of races) {
    const l = race.drivers?.legs?.find((x) => x.leg === leg);
    if (!race.drivers || !l?.boats) {continue;}
    const led = Object.entries(l.boats).find(([, b]) => b.led)?.[0] ?? "";
    // The leg as a race of its own: no roundings to divide by, never "measured early".
    asRaces.push({ ...race, legs: [], drivers: { ...race.drivers, boats: l.boats, winner: led, measured_at_leg: undefined } });
  }
  return asRaces.length ? dayDriversView(asRaces, sources) : null;
}

/** The day: each boat's parts averaged over the races it finished. */
export function dayDriversView(races: RaceTimelineRace[], sources: string[]): DriversView | null {
  const withDrivers = races.filter((r) => r.drivers);
  if (withDrivers.length === 0) {return null;}
  const want = sources.length ? new Set(sources) : null;
  const acc = new Map<string, {
    sum: DriverParts; gapM: number; gapS: number; mnvr: number; finished: number; raced: number; wins: number;
    ph: { inM: number; outM: number; pct: number; pctN: number; n: number };
  }>();
  const mpsVals: number[] = [];
  let roundings = 0;
  for (const race of withDrivers) {
    const d = race.drivers as RaceDrivers;
    if (d.course_mps) {mpsVals.push(d.course_mps);}
    roundings += Math.max(0, (race.legs?.length ?? 1) - 1);
    for (const [boat, b] of Object.entries(d.boats)) {
      if (want && !want.has(boat)) {continue;}
      const a = acc.get(boat) ?? {
        sum: { ...ZERO }, gapM: 0, gapS: 0, mnvr: 0, finished: 0, raced: 0, wins: 0,
        ph: { inM: 0, outM: 0, pct: 0, pctN: 0, n: 0 },
      };
      a.raced += 1;
      const p = partsOf(b);
      if (p) {
        a.finished += 1;
        for (const { key } of DRIVER_BUCKETS) {a.sum[key] += p[key];}
        a.gapM += b.gap_m ?? 0;
        a.gapS += b.gap_s ?? 0;
        a.mnvr += b.maneuvers ?? 0;
        if (boat === d.winner) {a.wins += 1;}
        const ph = phaseOf(b);
        if (ph) {
          a.ph.inM += ph.inM;
          a.ph.outM += ph.outM;
          a.ph.n += 1;
          if (ph.pct != null) {a.ph.pct += ph.pct; a.ph.pctN += 1;}
        }
      }
      acc.set(boat, a);
    }
  }
  const rows: DriverRow[] = [...acc.entries()].map(([boat, a]) => {
    const n = a.finished;
    const parts = n ? (Object.fromEntries(DRIVER_BUCKETS.map(({ key }) => [key, a.sum[key] / n])) as DriverParts) : null;
    return {
      boat,
      parts,
      gapM: n ? a.gapM / n : null,
      gapS: n ? a.gapS / n : null,
      maneuvers: n ? a.mnvr / n : null,
      phase: a.ph.n ? { inM: a.ph.inM / a.ph.n, outM: a.ph.outM / a.ph.n, pct: a.ph.pctN ? a.ph.pct / a.ph.pctN : null } : null,
      // Day reference: only a boat that won every race it finished is "the winner".
      isWinner: n > 0 && a.wins === n,
      finished: n,
      raced: a.raced,
    };
  });
  rows.sort(byGap);
  return {
    rows,
    spread: spreadOf(rows),
    mps: mpsVals.length ? mpsVals.reduce((x, y) => x + y, 0) / mpsVals.length : null,
    raceCount: withDrivers.length,
    roundingsPerRace: withDrivers.length ? roundings / withDrivers.length : 0,
    measuredEarly: withDrivers.flatMap(early),
  };
}

function early(race: RaceTimelineRace): { race: number; leg: number; legs: number }[] {
  const d = race.drivers;
  if (!d?.measured_at_leg || !d.legs_total || d.measured_at_leg >= d.legs_total) {return [];}
  return [{ race: race.race, leg: d.measured_at_leg, legs: d.legs_total }];
}

function byGap(a: DriverRow, b: DriverRow): number {
  if (a.parts && !b.parts) {return -1;}
  if (!a.parts && b.parts) {return 1;}
  return (a.gapM ?? 0) - (b.gapM ?? 0);
}

/** Buckets by spread, widest first: [0] decided it. */
export function decidingOrder(spread: DriverParts) {
  return [...DRIVER_BUCKETS].sort((a, b) => spread[b.key] - spread[a.key]);
}

export const secondsFor = (metres: number, mps: number | null): number | null =>
  mps && mps > 0 ? Math.round(Math.abs(metres) / mps) : null;

/**
 * EVENT SUMMARY: build a DriversView from per-team mean Gap_* parts (same panel as day RACE SUMMARY).
 * Teams with all-null gap parts are omitted. The reference (winner) is the team with the smallest mean gap.
 */
export function eventGapDriversView(
  teams: ReadonlyArray<{
    source_name: string;
    gap_start_m_mean?: number | null;
    gap_mnvr_m_mean?: number | null;
    gap_marks_m_mean?: number | null;
    gap_side_m_mean?: number | null;
    gap_speed_m_mean?: number | null;
  }>
): DriversView | null {
  const rowsRaw: DriverRow[] = [];
  for (const t of teams) {
    const parts: DriverParts = {
      start: Number(t.gap_start_m_mean ?? 0),
      mnvr: Number(t.gap_mnvr_m_mean ?? 0),
      marks: Number(t.gap_marks_m_mean ?? 0),
      side: Number(t.gap_side_m_mean ?? 0),
      speed: Number(t.gap_speed_m_mean ?? 0),
    };
    const any =
      t.gap_start_m_mean != null ||
      t.gap_mnvr_m_mean != null ||
      t.gap_marks_m_mean != null ||
      t.gap_side_m_mean != null ||
      t.gap_speed_m_mean != null;
    if (!any) continue;
    const gapM = parts.start + parts.mnvr + parts.marks + parts.side + parts.speed;
    rowsRaw.push({
      boat: t.source_name,
      parts,
      gapM,
      gapS: null,
      maneuvers: null,
      isWinner: false,
      phase: null,
      finished: 1,
      raced: 1,
    });
  }
  if (!rowsRaw.length) return null;
  rowsRaw.sort(byGap);
  const best = rowsRaw[0];
  const rows = rowsRaw.map((r) => ({
    ...r,
    isWinner: r.boat === best.boat,
    parts: r.boat === best.boat ? null : r.parts,
    gapM: r.boat === best.boat ? 0 : r.gapM,
  }));
  return {
    rows,
    spread: spreadOf(rows),
    mps: null,
    raceCount: 1,
    roundingsPerRace: 0,
    measuredEarly: [],
  };
}
